// src/features/inventory/pages/MSDSListPage.jsx
import React, { useEffect, useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Clear as ClearIcon,
  Error as ErrorIcon,
  OpenInNew as OpenIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../hooks/api/useApi';
import { msdsAPI } from '../services/msds';
import StaffMember from '../../../components/common/StaffMember';
import HazardDot, { HAZARD_LEVELS } from '../components/HazardDot';

import PageHeader from '../../../components/common/PageHeader';
import { ExplainTooltip, withInfo } from '../../../components/common/InfoTip';
import FeedIcon from '@mui/icons-material/Feed';

function StatusChip({ row, t }) {
  if (!row.is_valid) {
    return (
      <ExplainTooltip title={t('inventory:help.msds.invalid')}>
        <Chip icon={<ErrorIcon />} label={t('inventory:msds.status_chip.invalid')} color="error" size="small" variant="filled" />
      </ExplainTooltip>
    );
  }
  if (row.is_expired) {
    return <Chip icon={<ErrorIcon />} label={t('inventory:msds.status_chip.expired')} color="error" size="small" variant="filled" />;
  }
  if (row.is_expiring_soon) {
    return (
      <Chip
        icon={<WarningIcon />}
        label={t('inventory:msds.status_chip.expiring_soon', { days: row.days_until_review })}
        color="warning"
        size="small"
        variant="filled"
      />
    );
  }
  return <Chip icon={<CheckIcon />} label={t('inventory:msds.status_chip.valid')} color="success" size="small" variant="filled" />;
}

export default function MSDSListPage() {
  const { t } = useTranslation(['inventory']);
  const navigate = useNavigate();
  const { loading, error, execute, clearError } = useApi();

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [language, setLanguage] = useState('');
  const [hazardLevel, setHazardLevel] = useState('');

  const STATUS_OPTIONS = [
    { value: '', label: t('inventory:msds.status_options.all') },
    { value: 'valid', label: t('inventory:msds.status_options.valid') },
    { value: 'expiring_soon', label: t('inventory:msds.status_options.expiring_soon') },
    { value: 'expired', label: t('inventory:msds.status_options.expired') },
    { value: 'invalid', label: t('inventory:msds.status_options.invalid') },
  ];

  const LANGUAGE_OPTIONS = [
    { value: '', label: t('inventory:msds.language_options.all') },
    { value: 'da', label: t('inventory:msds.language_options.da') },
    { value: 'en', label: t('inventory:msds.language_options.en') },
  ];

  const fetchData = useCallback(async () => {
    const result = await execute(() =>
      msdsAPI.listAllMSDS({
        search: search || undefined,
        status: status || undefined,
        language: language || undefined,
        hazard_level: hazardLevel || undefined,
      })
    );
    if (result?.success) {
      setRows(result.data.data || []);
    }
  }, [execute, search, status, language, hazardLevel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const hasActiveFilters = Boolean(search || status || language || hazardLevel);

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setLanguage('');
    setHazardLevel('');
  };

  const columns = useMemo(
    () => [
      withInfo({
        field: 'status',
        headerName: t('inventory:msds.columns.status'),
        width: 190,
        sortable: false,
        renderCell: (params) => <StatusChip row={params.row} t={t} />,
      }, t('inventory:help.msds.status')),
      {
        field: 'item',
        headerName: t('inventory:msds.columns.product'),
        flex: 1,
        minWidth: 180,
        valueGetter: (value) => value?.name ?? '',
        renderCell: (params) => (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <HazardDot level={params.row.hazard_level} />
              <Typography variant="body2">{params.row.item?.name}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">{params.row.item?.sku}</Typography>
          </Box>
        ),
      },
      withInfo({
        field: 'version',
        headerName: t('inventory:msds.columns.version'),
        width: 90,
        renderCell: (params) => (
          <Chip label={`v${params.value}`} size="small" variant="outlined" color="info" />
        ),
      }, t('inventory:help.msds.version')),
      {
        field: 'language',
        headerName: t('inventory:msds.columns.language'),
        width: 80,
        renderCell: (params) => (
          <Chip label={params.value?.toUpperCase()} size="small" variant="outlined" />
        ),
      },
      withInfo({
        field: 'issued_date',
        headerName: t('inventory:msds.columns.issued_date'),
        width: 130,
        renderCell: (params) =>
          params.value ? new Date(params.value).toLocaleDateString('da-DK') : '—',
      }, t('inventory:help.msds.issued_date')),
      withInfo({
        field: 'review_date',
        headerName: t('inventory:msds.columns.review_date'),
        width: 150,
        renderCell: (params) =>
          params.value ? new Date(params.value).toLocaleDateString('da-DK') : '—',
      }, t('inventory:help.msds.review_date')),
      withInfo({
        field: 'days_until_review',
        headerName: t('inventory:msds.columns.days_until_review'),
        width: 190,
        renderCell: (params) => {
          const days = params.value;
          if (days == null) return '—';
          const color = days < 0 ? 'error' : days <= 30 ? 'warning' : 'text.secondary';
          return (
            <Typography variant="body2" color={color}>
              {days < 0
                ? t('inventory:msds.days_overdue', { days: Math.abs(days) })
                : t('inventory:msds.days_remaining', { days })}
            </Typography>
          );
        },
      }, t('inventory:help.msds.days_until_review')),
      {
        field: 'creator',
        headerName: t('inventory:msds.columns.created_by'),
        width: 180,
        sortable: false,
        renderCell: (params) => {
          if (!params.value) return '—';
          return (
            <StaffMember
              staffMember={params.value}
              avatarSize={28}
              imageSize="32x32"
              nameVariant="body2"
              gap={1}
              clickable
            />
          );
        },
      },
      {
        field: 'actions',
        renderHeader: () => <></>,
        width: 52,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title={t('inventory:msds.go_to_product')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/inventory/items/${params.row.item_id}?tab=hazardous`);
              }}
            >
              <OpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate, t]
  );

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">

      {/* Header */}
      <PageHeader
        icon={FeedIcon}
        title={t('inventory:msds.page_title')}
        subtitle={t('inventory:msds.page_subtitle')}
        actions={<>
          <Tooltip title={t('inventory:msds.refresh')}>
            <IconButton onClick={fetchData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </>}
      />

      {/* Filter bar */}
      <Box mb={3} display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder={t('inventory:msds.search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="m-s-d-s-list-page-status-label-label">{t('inventory:msds.status_label')}</InputLabel>
          <Select labelId="m-s-d-s-list-page-status-label-label" value={status} onChange={(e) => setStatus(e.target.value)} label={t('inventory:msds.status_label')}>
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="m-s-d-s-list-page-language-label-label">{t('inventory:msds.language_label')}</InputLabel>
          <Select labelId="m-s-d-s-list-page-language-label-label" value={language} onChange={(e) => setLanguage(e.target.value)} label={t('inventory:msds.language_label')}>
            {LANGUAGE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="m-s-d-s-list-page-hazard-level-label">{t('inventory:msds.hazard_level.label')}</InputLabel>
          <Select labelId="m-s-d-s-list-page-hazard-level-label" value={hazardLevel} onChange={(e) => setHazardLevel(e.target.value)} label={t('inventory:msds.hazard_level.label')}>
            <MenuItem value="">{t('inventory:msds.hazard_level.all')}</MenuItem>
            {HAZARD_LEVELS.map((level) => (
              <MenuItem key={level} value={level}>
                <HazardDot level={level} tooltip={false} sx={{ mr: 2 }} />
                {t(`inventory:msds.hazard_level.${level}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasActiveFilters && (
          <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
            {t('inventory:msds.clear_filters')}
          </Button>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.msds_id}
          loading={loading}
          onRowClick={(params) => navigate(`/inventory/items/${params.row.item_id}?tab=hazardous`)}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableColumnFilter
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell': {
              minHeight: '52px !important',
              display: 'flex !important',
              alignItems: 'center !important',
              padding: '8px 16px !important',
            },
            minHeight: '70vh',
          }}
        />
      </Box>

    </Box>
  );
}
